import { useCallback, useState } from 'react';
import classnames from 'classnames';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { CollapsibleListSection } from 'ui-components/components/collapsible-list/collapsible-list-section';
import { CollapsibleListSectionContent } from 'ui-components/components/collapsible-list/collapsible-list-section-content';
import { CollapsibleListSectionHeader } from 'ui-components/components/collapsible-list/collapsible-list-section-header';
import { DatePicker } from 'ui-components/components/date-picker';
import { FlexColumn, FlexRow, Gap } from 'ui-components/components/flex';
import { FloatTextArea } from 'ui-components/components/float-text-area';
import { Input, version2 } from 'ui-components/components/input';
import { Text, TextType } from 'ui-components/components/text';

import Global from '../../../../../../store/global-selectors';
import { DetailField } from '../../../components/detail-field/detail-field';
import { messages } from '../../messages';
import styles from './shipping-details-edit.styles.module.less';

const NOTES_MAX_LENGTH = 2000;

export type ShippingDetailsEditFormData = {
  referenceNumber: string | null;
  orderDate: string | null;
  expectedDeliveryDate: string | null;
  notes: string | null;
};

type ShippingDetailsEditProps = {
  orderNumber: string;
  referenceNumber: string | null;
  orderDate: string | null;
  expectedDeliveryDate: string | null;
  notes: string | null;
  onFormDataChange: (updates: Partial<ShippingDetailsEditFormData>) => void;
};

export const ShippingDetailsEdit = ({
  orderNumber,
  referenceNumber: initialReferenceNumber,
  orderDate: initialOrderDate,
  expectedDeliveryDate: initialExpectedDeliveryDate,
  notes: initialNotes,
  onFormDataChange,
}: ShippingDetailsEditProps) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const { formatMessage } = useIntl();
  const dateFormat = useSelector(Global.selectUserUserDateFormat) as string;

  const [referenceNumber, setReferenceNumber] = useState(initialReferenceNumber ?? '');
  const [notes, setNotes] = useState(initialNotes ?? '');

  const toggleIsExpanded = useCallback(() => {
    setIsExpanded((prev) => !prev);
  }, []);

  const onSectionHeaderClick = useCallback(
    (e: React.MouseEvent) => {
      if (!e?.defaultPrevented) {
        toggleIsExpanded();
      }
    },
    [toggleIsExpanded],
  );

  const headerContent = <Text textType={TextType.H4} value={messages.shippingDetails} />;

  return (
    <section aria-label={formatMessage(messages.shippingDetails)} className={styles['shipping-details-wrapper']}>
      <CollapsibleListSection open={isExpanded}>
        <CollapsibleListSectionHeader
          className={classnames(styles.header, styles['expandable-header-container'])}
          contentClassName={styles['header-content']}
          onClick={onSectionHeaderClick}
          theme='white'
        >
          {headerContent}
        </CollapsibleListSectionHeader>
        <CollapsibleListSectionContent className={styles['expandable-content-container']}>
          <div>
            <FlexRow className={styles['details-grid']}>
              <DetailField className={styles['details-column']} label={messages.orderId} value={orderNumber} />

              <FlexColumn className={styles['details-column']} gap={Gap.Space4}>
                <Text textType={TextType.Label} value={messages.referenceNumber} />
                <Input
                  name='referenceNumber'
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setReferenceNumber(e.target.value);
                    onFormDataChange({ referenceNumber: e.target.value || null });
                  }}
                  value={referenceNumber}
                  version={version2}
                />
              </FlexColumn>
            </FlexRow>

            <FlexRow className={styles['details-grid']}>
              <FlexColumn className={styles['details-column']} gap={Gap.Space4}>
                <Text textType={TextType.Label} value={messages.orderDate} />
                <DatePicker
                  allowPastDates
                  calendarPosition='bottom-right'
                  className={styles['date-picker']}
                  dateFormat={dateFormat}
                  onChange={(value: string) => onFormDataChange({ orderDate: value || null })}
                  placeholder=''
                  shouldInputHaveFullWidth
                  value={initialOrderDate ?? ''}
                />
              </FlexColumn>

              <FlexColumn className={styles['details-column']} gap={Gap.Space4}>
                <Text textType={TextType.Label} value={messages.expectedDeliveryDate} />
                <DatePicker
                  allowPastDates
                  calendarPosition='bottom-right'
                  className={styles['date-picker']}
                  dateFormat={dateFormat}
                  onChange={(value: string) => onFormDataChange({ expectedDeliveryDate: value || null })}
                  placeholder=''
                  shouldInputHaveFullWidth
                  value={initialExpectedDeliveryDate ?? ''}
                />
              </FlexColumn>
            </FlexRow>

            <FlexRow className={styles['details-grid']}>
              <FlexColumn className={styles['full-width-column']} gap={Gap.Space4}>
                <Text textType={TextType.Label} value={messages.notes} />
                <div className={styles['notes-input-wrapper']}>
                  <FloatTextArea
                    maxLength={NOTES_MAX_LENGTH}
                    onChange={(value: string) => {
                      setNotes(value);
                      onFormDataChange({ notes: value || null });
                    }}
                    placeholder={`0/${NOTES_MAX_LENGTH}`}
                    value={notes}
                  />
                </div>
              </FlexColumn>
            </FlexRow>
          </div>
        </CollapsibleListSectionContent>
      </CollapsibleListSection>
    </section>
  );
};
